import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import {
  VetaCenterReferenceKeyMatchableType,
  VetaCenterReferenceKeyQualifier,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useRef, useState } from 'react';

import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import useVetaCenter, {
  readReferenceKeyRejection,
} from '~hooks/useVetaCenter/useVetaCenter';
import useVetaCenterSourceOperatorsQuery from '~queries/useVetaCenterSourceOperatorsQuery';

interface InlineEditorProps {
  companyId: string;
  onSaved: () => void;
  recordId: string;
}

interface ReferenceKeyEditorProps extends InlineEditorProps {
  helperText?: string | null;
  isFieldError?: boolean;
  label: string;
  matchableType: VetaCenterReferenceKeyMatchableType;
  onOpen?: () => void;
  options?: { label: string; value: string }[];
  qualifier: VetaCenterReferenceKeyQualifier;
}

const KEY_PREFIX = 'administration.integration.veta_center.readiness';

function ReferenceKeyEditor({
  companyId,
  helperText,
  isFieldError = false,
  label,
  matchableType,
  onOpen,
  onSaved,
  options,
  qualifier,
  recordId,
}: ReferenceKeyEditorProps) {
  const { setReferenceKey } = useVetaCenter();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const isMutatingRef = useRef(false);
  const trimmedValue = value.trim();

  const handleSave = async () => {
    if (isMutatingRef.current) return;
    isMutatingRef.current = true;

    setIsSaving(true);
    setError(null);
    try {
      const isSaved = await setReferenceKey(companyId, {
        matchableId: recordId,
        matchableType,
        qualifier,
        value: trimmedValue,
      });
      if (isSaved) onSaved();
    } catch (rejection) {
      setError(readReferenceKeyRejection(rejection));
    } finally {
      isMutatingRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 1 }}>
      <TextField
        slotProps={{ select: { onOpen } }}
        error={isFieldError || Boolean(error)}
        helperText={helperText ?? error}
        label={label}
        onChange={(event) => setValue(event.target.value)}
        select={Boolean(options)}
        size="small"
        sx={{ minWidth: options ? 220 : undefined }}
        value={value}
      >
        {options?.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <SmallLoadingButton
        color="brandV2Black"
        disabled={!trimmedValue}
        loading={isSaving}
        onClick={handleSave}
        sx={{ borderColor: 'divider', color: 'text.primary', mt: 0.5 }}
        variant="outlined"
      >
        {t('actions.save')}
      </SmallLoadingButton>
    </Box>
  );
}

export function MaterialCodeEditor(props: InlineEditorProps) {
  return (
    <ReferenceKeyEditor
      {...props}
      label={t(`${KEY_PREFIX}.material_code_label`)}
      matchableType={VetaCenterReferenceKeyMatchableType.MATERIAL}
      qualifier={VetaCenterReferenceKeyQualifier.VETACENTER_MATERIAL_CODE}
    />
  );
}

export function SourceOperatorEditor(props: InlineEditorProps) {
  const [isRequested, setIsRequested] = useState(false);
  const { isError, isFetching, operators, refetch } = useVetaCenterSourceOperatorsQuery(
    props.companyId,
    props.recordId,
    isRequested,
  );

  const getHelperText = () => {
    if (isError) {
      return t(`${KEY_PREFIX}.weighmaster_unreachable`);
    }
    if (isFetching) {
      return t(`${KEY_PREFIX}.weighmaster_loading`);
    }

    return null;
  };

  return (
    <ReferenceKeyEditor
      {...props}
      helperText={getHelperText()}
      isFieldError={isError}
      label={t(`${KEY_PREFIX}.weighmaster_label`)}
      matchableType={VetaCenterReferenceKeyMatchableType.SITE}
      onOpen={() => {
        if (!isRequested) setIsRequested(true);
        else if (isError) refetch();
      }}
      options={operators.map((operator) => ({
        label: operator.name ?? operator.guid,
        value: operator.guid,
      }))}
      qualifier={VetaCenterReferenceKeyQualifier.VETACENTER_SOURCE_OPERATOR}
    />
  );
}
